import * as React from "react";
import * as SeparatorPrimitive from "@radix-ui/react-separator";
import { cn } from "../../lib/utils";

/**
 * **Last-resort primitive.** Under the borderless system separation comes from
 * surface, shadow, radius and space — a drawn line is no longer a legitimate way
 * to say "different thing" (`docs/borderless-contract.md` §0). Reach for `gap-*`,
 * a tier change, or a `text-xs uppercase` eyebrow first; use `Separator` only
 * where there is genuinely no space to spend (dense toolbars, menu panels).
 *
 * Weight is pinned to `line-subtle` so it can never read as a hairline border.
 */
const Separator = React.forwardRef<
  React.ComponentRef<typeof SeparatorPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof SeparatorPrimitive.Root>
>(
  (
    { className, orientation = "horizontal", decorative = true, ...props },
    ref,
  ) => (
    <SeparatorPrimitive.Root
      ref={ref}
      decorative={decorative}
      orientation={orientation}
      className={cn(
        "shrink-0 bg-line-subtle",
        orientation === "horizontal" ? "h-px w-full" : "h-full w-px",
        className,
      )}
      {...props}
    />
  ),
);
Separator.displayName = SeparatorPrimitive.Root.displayName;

export { Separator };
